import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CTABanner from '../../components/CTABanner';
import { usePageMeta, useStructuredData } from '../../lib/seo';
import { SITE_URL } from '../../config/productFacts';
import CITIES from '../../data/tankstellenCities.json';
import { StationList, DataAttribution, fmtPrice } from '../tankstellen/tankstellenShared';
import { ClusterHero, FaqSection, CrossLinkChips, FuelXBox, breadcrumbSchema, faqSchema } from './clusterShared';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DieselpreiseCityPage() {
  const { slug } = useParams();
  const city = useMemo(() => CITIES.find((c) => c.slug === slug), [slug]);
  const name = city?.name || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageMeta(
    `Dieselpreise ${name} aktuell: Heute günstig Diesel tanken | FuelX`,
    `Aktueller Dieselpreis in ${name} im Live-Vergleich: günstigste Diesel-Tankstelle, DE-Vergleich & Pendler-Rechnung — amtliche MTS-K-Daten, alle 15 Minuten aktualisiert.`,
    { canonical: `${SITE_URL}/dieselpreise/${slug}` }
  );

  useEffect(() => {
    if (!slug || !city) return;
    let active = true;
    setLoading(true);
    setData(null);
    axios
      .get(`${API}/api/tankstellen/city/${slug}`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ status: 'error', stations: [], stats: {} }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, city]);

  const faq = useMemo(() => [
    [`Wie hoch ist der Dieselpreis in ${name} heute?`, `Den aktuellen Dieselpreis für ${name} siehst du oben — live aus der amtlichen Markttransparenzstelle für Kraftstoffe (MTS-K), alle 15 Minuten aktualisiert.`],
    [`Wo tanke ich Diesel in ${name} am günstigsten?`, `Die günstigste Diesel-Tankstelle wird oben hervorgehoben, darunter folgen die 10 besten Diesel-Preise in und um ${name} — sortiert vom niedrigsten Preis aufwärts.`],
    [`Wann ist Diesel in ${name} am billigsten?`, 'Abends zwischen 18 und 22 Uhr — im Schnitt 8 bis 14 Cent günstiger als morgens. Für Pendler lohnt es sich, abends auf dem Heimweg zu tanken statt morgens auf dem Hinweg.'],
  ], [name]);

  const schema = useMemo(() => {
    if (!city) return null;
    return {
      '@graph': [
        breadcrumbSchema([['FuelX', ''], ['Dieselpreise', '/dieselpreise'], [`Dieselpreise ${city.name}`, `/dieselpreise/${city.slug}`]]),
        faqSchema(faq),
      ],
    };
  }, [city, faq]);
  useStructuredData(schema, `dieselpreise-${slug}`);

  if (!city) return <Navigate to="/dieselpreise" replace />;

  const st = data?.stats?.diesel;
  const nat = data?.national?.diesel;
  const deltaCt = st && typeof nat === 'number' ? Math.round((st.avg - nat) * 100) : null;
  const spreadCt = st ? Math.round((st.max - st.min) * 100) : null;
  // Pendler-Beispiel: 40 km/Tag · 6,5 l/100 km · 220 Arbeitstage
  const pendlerSavings = spreadCt ? ((spreadCt / 100) * (40 * 220 * 6.5) / 100).toFixed(0) : null;
  const neighbors = data?.neighbors || [];

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid="dieselpreise-city-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-16">
        <ClusterHero
          crumbs={[['FuelX', '/'], ['Dieselpreise', '/dieselpreise'], [name, null]]}
          eyebrow={`${city.bundesland ? `${city.bundesland} · ` : ''}Live-Dieselpreise`}
          h1={<>Dieselpreise {name}:<br />Heute günstig Diesel tanken</>}
          intro={`Der aktuelle Dieselpreis für ${name} im Live-Vergleich — mit der günstigsten Diesel-Tankstelle, dem Vergleich zum Bundesdurchschnitt und einer ehrlichen Pendler-Rechnung. Amtliche MTS-K-Daten, alle 15 Minuten aktualisiert.`}
          testId="dp-city"
        />

        {/* Diesel-Stat */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10" data-testid="dp-city-stats">
          <div className="rounded-2xl border border-stone-900 bg-stone-950 p-5 text-white" data-testid="dp-city-min">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-400">Diesel · günstigster Preis</p>
            <p className="mt-2 text-3xl font-medium tabular-nums tracking-tight">{loading ? '···' : fmtPrice(st?.min)}</p>
            {st?.cheapest_station && (
              <p className="mt-1.5 text-[12px] text-stone-400 truncate">{st.cheapest_station.brand || st.cheapest_station.name}{st.cheapest_station.street ? ` · ${st.cheapest_station.street}` : ''}</p>
            )}
          </div>
          <div className="rounded-2xl border border-stone-200 p-5" data-testid="dp-city-avg">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Ø {name}</p>
            <p className="mt-2 text-3xl font-medium tabular-nums tracking-tight text-stone-900">{loading ? '···' : fmtPrice(st?.avg)}</p>
            {deltaCt !== null && (
              <p className={`mt-1.5 text-[12px] font-semibold ${deltaCt <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {deltaCt <= 0 ? `${Math.abs(deltaCt)} ct unter` : `${deltaCt} ct über`} Bundesdurchschnitt
              </p>
            )}
          </div>
          <div className="rounded-2xl border border-stone-200 p-5" data-testid="dp-city-spread">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Preisspanne heute</p>
            <p className="mt-2 text-3xl font-medium tabular-nums tracking-tight text-stone-900">{loading || spreadCt === null ? '···' : `${spreadCt} ct`}</p>
            <p className="mt-1.5 text-[12px] text-stone-500">zwischen günstigster & teuerster Station</p>
          </div>
        </div>

        {/* Top-10 Diesel */}
        <div className="mt-12">
          <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-5">
            Die 10 günstigsten Diesel-Tankstellen in {name}
          </h2>
          {loading ? (
            <div className="rounded-2xl border border-stone-200 divide-y divide-stone-100 overflow-hidden animate-pulse" data-testid="dp-city-loading">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-stone-50" />)}
            </div>
          ) : (
            <StationList stations={data?.stations || []} fuel="diesel" limit={10} testIdPrefix="dp-city-station" />
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <DataAttribution fetchedAt={data?.fetched_at} />
            <Link to={`/tankstellen/${slug}`} className="text-[13px] font-semibold text-emerald-700 hover:text-emerald-900 transition-colors" data-testid="dp-city-all-stations-link">
              Alle Tankstellen in {name} mit Karte →
            </Link>
          </div>
        </div>

        {/* Pendler-Rechnung */}
        <section className="mt-14 text-[15.5px] text-stone-600 leading-[1.75]" data-testid="dp-city-pendler">
          <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-4">
            Die Pendler-Rechnung für {name}
          </h2>
          <p className="mb-4">
            Beispiel: 40 km Arbeitsweg pro Tag, 6,5 l/100 km, 220 Arbeitstage — das sind rund 572 Liter Diesel pro Jahr nur fürs Pendeln.
            {spreadCt !== null && spreadCt > 0 && pendlerSavings ? (
              <> Bei der aktuellen Preisspanne von <strong className="text-stone-900">{spreadCt} Cent</strong> in {name} macht allein die Wahl der richtigen Tankstelle bis zu <strong className="text-emerald-700">{pendlerSavings} € pro Jahr</strong> aus.</>
            ) : (
              <> Schon 10 Cent Preisunterschied machen dabei über 57 € pro Jahr aus — nur durch die Wahl der richtigen Tankstelle.</>
            )}
          </p>
          <p>
            Deine persönliche Rechnung machst du in 30 Sekunden mit dem <Link to="/spritkostenrechner" className="font-semibold text-emerald-700 hover:text-emerald-900">Spritkostenrechner</Link>.
          </p>
        </section>

        <FuelXBox title="Für Diesel-Vielfahrer: die FuelX Card" testId="dp-city-fuelx-box">
          <p>
            Mit der FuelX Card sparst du bei aktiver Nutzung qualifizierender Angebote effektiv <strong>Ø 0,40 € pro Liter</strong> — und jeder gefahrene Kilometer bringt zusätzlich FuelGood-Guthaben. Businesskunden managen die ganze Flotte über die Gold Card.
          </p>
        </FuelXBox>

        <FaqSection title={`Häufige Fragen zu Dieselpreisen in ${name}`} items={faq} testId="dp-city-faq" />

        {neighbors.length > 0 && (
          <CrossLinkChips
            title="Dieselpreise in der Umgebung"
            links={neighbors.map((n) => [`Dieselpreise ${n.name}`, `/dieselpreise/${n.slug}`])}
            testId="dp-city-neighbors"
          />
        )}

        <CrossLinkChips
          links={[
            [`Spritpreise ${name} (alle Sorten)`, `/spritpreise/${slug}`],
            [`Alle Tankstellen in ${name} + Karte`, `/tankstellen/${slug}`],
            ['Dieselpreise Deutschland', '/dieselpreise'],
            ['Tankkarte für Firmen & Flotten', '/tankkarte-firmen'],
            ['Spritkostenrechner', '/spritkostenrechner'],
          ]}
          testId="dp-city-links"
        />
      </div>

      <CTABanner
        variant="light"
        headline={<>Vielfahrer in {name} sparen doppelt.<br /><span className="text-stone-400">Pro Liter. Pro Kilometer.</span></>}
        text="Mit der FuelX Card sparst du effektiv Ø 0,40 € pro Liter — und jeder Kilometer bringt FuelGood-Guthaben."
        buttonText="FuelX Card entdecken"
        buttonTo="/fuelx-card"
      />
    </div>
  );
}
